'use client'

import ReactTimeAgo from 'react-time-ago'

import type { RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getIndependenceTier } from '@helper/utils/business-logic'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

export type LessonTrendStudent = RouterOutputs['metrics']['getLessonTrendStudents']['students'][number]

interface StudentCardProps {
  student: LessonTrendStudent
  renderProfileLink: RenderLink
}

export function StudentCard({ student, renderProfileLink }: StudentCardProps) {
  const { tugasCount, latihanCount, perSubjectXp, badgeXp } = student
  const { label: tierLabel } = getIndependenceTier({ tugasCount, latihanCount })

  const total = tugasCount + latihanCount
  const tugasShare = total > 0 ? tugasCount / total : 0
  const latihanShare = total > 0 ? latihanCount / total : 0

  // "Belajar mandiri" headline: latihan/tugas multiplier; 100% when there is no assigned work at all.
  const mandiriValue =
    tugasCount === 0
      ? latihanCount > 0
        ? '100%'
        : '—'
      : `${(latihanCount / tugasCount).toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}x`

  // Highlight the student's strongest subject (first on ties). No highlight when no XP earned anywhere.
  const topXp = perSubjectXp.reduce((max, s) => Math.max(max, s.xp), 0)
  const topSubjectId = topXp > 0 ? perSubjectXp.find((s) => s.xp === topXp)?.subjectId : undefined

  return (
    <article className='srd:flex srd:flex-col srd:gap-4 srd:rounded-xl srd:border srd:border-grey-30 srd:bg-neutral-white srd:p-4'>
      {/* Header: identity + tier badge */}
      <header className='srd:flex srd:items-start srd:justify-between srd:gap-2'>
        <div className='srd:flex srd:min-w-0 srd:items-center srd:gap-x-2'>
          <Avatar profile='user' className='srd:size-8 srd:shrink-0'>
            <AvatarImage src={student.image ?? undefined} />
            <AvatarFallback>{getInitials(student.name)}</AvatarFallback>
          </Avatar>
          <div className='srd:flex srd:min-w-0 srd:flex-col'>
            {renderProfileLink({
              children: student.name,
              className: cn(typographyVariants({ variant: 'body-lg' }), 'srd:hover:underline srd:truncate'),
              meta: { childId: student.id, back_redirect: 'lesson-trend' },
            })}
            <span className={cn(typographyVariants({ variant: 'body-sm' }))}>
              {student.lastActiveAt ? (
                <>
                  Aktif <ReactTimeAgo date={new Date(student.lastActiveAt)} />
                </>
              ) : (
                'Belum pernah login'
              )}
            </span>
          </div>
        </div>

        <span
          className={cn(
            typographyVariants({ variant: 'label-sm' }),
            'srd:shrink-0 srd:whitespace-nowrap srd:rounded-md srd:border srd:px-2 srd:py-1 srd:border-outline-variant/50 srd:text-grey-100',
          )}
        >
          {tierLabel}
        </span>
      </header>

      <div className='srd:grid srd:grid-cols-1 srd:gap-4 srd:lg:grid-cols-2'>
        {/* Belajar mandiri */}
        <section className='srd:flex srd:flex-col srd:gap-2'>
          <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-grey-100')}>Kemandirian belajar</p>
          <p className='srd:flex srd:flex-wrap srd:items-baseline srd:gap-x-1.5'>
            <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold srd:text-neutral-black')}>
              {mandiriValue}
            </span>
            {(tugasCount > 0 || latihanCount > 0) && (
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
                di luar tugas guru
              </span>
            )}
          </p>

          {/* Two-tone proportion bar: tugas (dark) vs latihan (light) */}
          <div className='srd:flex srd:h-3 srd:w-full srd:overflow-hidden srd:rounded-full srd:bg-grey-30'>
            <span className='srd:bg-navy-100' style={{ width: `${tugasShare * 100}%` }} />
            <span className='srd:bg-blue-70' style={{ width: `${latihanShare * 100}%` }} />
          </div>

          <div
            className={cn(
              typographyVariants({ variant: 'body-sm' }),
              'srd:flex srd:flex-wrap srd:items-center srd:gap-x-3 srd:gap-y-1 srd:text-grey-100',
            )}
          >
            <span className='srd:flex srd:items-center srd:gap-1.5'>
              <span className='srd:size-4 srd:rounded-[4px] srd:bg-navy-100' />
              {tugasCount.toLocaleString('id-ID')} tugas
            </span>
            <span className='srd:flex srd:items-center srd:gap-1.5'>
              <span className='srd:size-4 srd:rounded-[4px] srd:bg-blue-70' />
              {latihanCount.toLocaleString('id-ID')} latihan
            </span>
          </div>
        </section>

        {/* XP per Mapel */}
        <section className='srd:flex srd:flex-col srd:gap-2'>
          <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-grey-100')}>XP per Mapel</p>
          {perSubjectXp.length > 0 ? (
            <div className='srd:flex srd:flex-wrap srd:gap-2'>
              {perSubjectXp.map((subject) => {
                const isTop = subject.subjectId === topSubjectId
                return (
                  <div
                    key={subject.subjectId}
                    className={cn(
                      'srd:flex srd:min-w-[96px] srd:flex-1 srd:flex-col srd:gap-0.5 srd:rounded-xl srd:border srd:p-4',
                      isTop ? 'srd:border-outline srd:bg-neutral-white' : 'srd:border-grey-20 srd:bg-grey-20',
                    )}
                  >
                    <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:truncate srd:text-grey-100')}>
                      {subject.name}
                    </span>
                    <span
                      className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold srd:text-neutral-black')}
                    >
                      {subject.xp > 0 ? subject.xp.toLocaleString('id-ID') : '-'}
                    </span>
                  </div>
                )
              })}
              {/* Badge XP belongs to no subject, but it IS part of Total XP — without a tile of its own
                  the tiles would not add up to the total shown when sorting by Total XP. */}
              {badgeXp > 0 && (
                <div
                  key='badge-xp'
                  className='srd:flex srd:min-w-[96px] srd:flex-1 srd:flex-col srd:gap-0.5 srd:rounded-xl srd:border srd:border-grey-20 srd:bg-grey-20 srd:p-4'
                >
                  <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:truncate srd:text-grey-100')}>
                    Lencana
                  </span>
                  <span
                    className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold srd:text-neutral-black')}
                  >
                    {badgeXp.toLocaleString('id-ID')}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>Belum ada XP</p>
          )}
        </section>
      </div>
    </article>
  )
}
